import { Box, Tabs, Tab, Typography } from '@mui/material';
import { useState, useRef } from 'react';

import { EditRequestFieldCard } from '../../index';

export function EditRequestComparisonTable({ comparisonSections }) {
  const [activeTab, setActiveTab] = useState('basicinfo');

  const activeRows = comparisonSections[activeTab] || [];
  const oldRef = useRef(null);
  const newRef = useRef(null);

  const listTabs = [
    'empaddress',
    'emprelations',
    'empeducation',
    'empcivservice',
    'empworkexp',
    'empcivicwork',
    'emptraining',
  ];

  // Sync scroll between the two boxes
  const syncScroll = (source) => {
    if (source === 'old' && oldRef.current && newRef.current) {
      newRef.current.scrollTop = oldRef.current.scrollTop;
    } else if (source === 'new' && oldRef.current && newRef.current) {
      oldRef.current.scrollTop = newRef.current.scrollTop;
    }
  };

  const scrollStyle = {
    // Scroll style WebKit browsers
    '&::-webkit-scrollbar': { width: '8px' },
    '&::-webkit-scrollbar-track': {
      background: '#2D3748',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#718096',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': { background: '#A0AEC0' },

    // Scroll style (Firefox)
    scrollbarWidth: 'thin',
    scrollbarColor: '#718096 #2D3748',
  };

  const comparisonBoxStyle = {
    // Styles
    bgcolor: '#1A202C',
    p: 2,
    borderRadius: '3px',
    height: '100%',
    width: '100%',
    overflowY: 'auto',
  };

  const comparisonFieldSizeStyle = {
    height: '50px',
    maxHeight: '50px',
    overflowY: 'auto',
    mb: 1.5,
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        height: '100%',
        maxHeight: '90%',
        minHeight: 0,
      }}
    >
      {/* LEFT TABS */}
      <Box
        sx={{
          maxHeight: '100%',
          overflowY: 'auto',
          width: '200px',
          ...scrollStyle,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          orientation="vertical"
          sx={{
            borderRight: 1,
            borderColor: 'divider',
            minWidth: 150,
          }}
        >
          <Tab label="Basic Info" value="basicinfo" />
          <Tab label="Personal Info" value="personalinfo" />
          <Tab label="Address" value="empaddress" />
          <Tab label="Relations" value="emprelations" />
          <Tab label="Education" value="empeducation" />
          <Tab label="Civil Service" value="empcivservice" />
          <Tab label="Work Experience" value="empworkexp" />
          <Tab label="Civic Work" value="empcivicwork" />
          <Tab label="Training" value="emptraining" />
          <Tab label="History" value="emphistory" />
        </Tabs>
      </Box>

      {/* RIGHT SECTION */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          gap: 2,
          px: 2,
        }}
      >
        {/* OLD DATA */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            gap: 0.5,
          }}
        >
          <Typography align="center">Current Data</Typography>
          <Box
            sx={{ ...comparisonBoxStyle, ...scrollStyle }}
            ref={oldRef}
            onScroll={() => syncScroll('old')}
          >
            {listTabs.includes(activeTab) ? (
              <EditRequestFieldCard
                activeRows={activeRows.old}
                activeTab={activeTab}
                tag="old"
              />
            ) : (
              activeRows.map((row, index) => (
                <Box sx={comparisonFieldSizeStyle} key={index}>
                  <Typography
                    key={row.label}
                    data-testid={`comparison-old-${activeTab}-${index}`}
                  >
                    {row.label}: {row.original}
                  </Typography>
                </Box>
              ))
            )}
          </Box>
        </Box>

        {/* NEW DATA */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            gap: 0.5,
          }}
        >
          <Typography align="center">New Data</Typography>
          <Box
            sx={{ ...comparisonBoxStyle, ...scrollStyle }}
            ref={newRef}
            onScroll={() => syncScroll('new')}
          >
            {listTabs.includes(activeTab) ? (
              <EditRequestFieldCard
                activeRows={activeRows.new}
                activeTab={activeTab}
                tag="new"
              />
            ) : (
              activeRows.map((row, index) => (
                <Box sx={comparisonFieldSizeStyle} key={index}>
                  <Typography
                    key={row.label}
                    sx={{
                      color: row.isdifferent ? 'yellow' : '',
                    }}
                    data-testid={`comparison-new-${activeTab}-${index}`}
                  >
                    {row.label}: {row.proposed}
                  </Typography>
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
